/**
 * Recently viewed records (TMMPDM-0040): the last 8 tenant, reseller and affiliate pages opened, newest
 * first, de-duplicated — shown by the command palette on an empty input. UI recents, never a
 * credential: business names and slugs (a reseller's key is its id) under the one sanctioned key
 * `tmm_product_admin.recent`, allowlisted by `src/storage.guard.test.ts`. Labels only — opening a
 * recent re-reads the record, and one that no longer resolves (404) is forgotten.
 * Storage can be unavailable (private mode, blocked site data), so every access is guarded and the
 * palette simply shows no recents.
 */
import { useCallback, useState } from 'react';

export const RECENT_KEY = 'tmm_product_admin.recent';
export const RECENT_MAX = 8;

export type RecentType = 'partner' | 'tenant' | 'reseller' | 'affiliate';

export interface RecentEntry {
  type: RecentType;
  /** Tenant slug, or the reseller id as a string — what the detail route addresses. */
  key: string;
  /** The record's name when it was last opened. */
  label: string;
}

function isRecentEntry(value: unknown): value is RecentEntry {
  if (value === null || typeof value !== 'object') return false;
  const entry = value as Record<string, unknown>;
  return (
    (entry.type === 'partner' ||
      entry.type === 'tenant' ||
      entry.type === 'reseller' ||
      entry.type === 'affiliate') &&
    typeof entry.key === 'string' &&
    entry.key !== '' &&
    typeof entry.label === 'string'
  );
}

/** The stored list; anything unreadable or malformed reads as empty. */
export function readRecent(): RecentEntry[] {
  try {
    const raw = window.localStorage.getItem(RECENT_KEY);
    if (raw === null) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    const seen = new Set<string>();
    return parsed
      .filter(isRecentEntry)
      .filter((entry) => {
        const identity = `${entry.type === 'reseller' ? 'partner' : entry.type}:${entry.key}`;
        if (seen.has(identity)) return false;
        seen.add(identity);
        return true;
      })
      .slice(0, RECENT_MAX)
      .map(({ type, key, label }) => ({ type, key, label }));
  } catch {
    return [];
  }
}

function writeRecent(entries: RecentEntry[]): void {
  try {
    window.localStorage.setItem(RECENT_KEY, JSON.stringify(entries));
  } catch {
    /* recents not persisted — the palette still works without them */
  }
}

const same = (a: RecentEntry, type: RecentType, key: string) =>
  a.key === key &&
  (a.type === type ||
    ((a.type === 'partner' || a.type === 'reseller') &&
      (type === 'partner' || type === 'reseller')));

/** Puts the record first, dropping any older entry for it, and keeps the newest 8. */
export function recordRecent(entry: RecentEntry): void {
  const rest = readRecent().filter((existing) => !same(existing, entry.type, entry.key));
  writeRecent([entry, ...rest].slice(0, RECENT_MAX));
}

/** Removes a record that no longer resolves; true when it was in the list. */
export function forgetRecent(type: RecentType, key: string): boolean {
  const entries = readRecent();
  const rest = entries.filter((existing) => !same(existing, type, key));
  if (rest.length === entries.length) return false;
  writeRecent(rest);
  return true;
}

/**
 * The recents as read when the caller mounted (the palette mounts per open, so each open re-reads),
 * plus `record` and `forget`, which write through and keep that snapshot current.
 */
export function useRecent(): {
  entries: RecentEntry[];
  record: (entry: RecentEntry) => void;
  forget: (type: RecentType, key: string) => boolean;
} {
  const [entries, setEntries] = useState(readRecent);
  const record = useCallback((entry: RecentEntry) => {
    recordRecent(entry);
    setEntries(readRecent());
  }, []);
  const forget = useCallback((type: RecentType, key: string) => {
    const removed = forgetRecent(type, key);
    if (removed) setEntries(readRecent());
    return removed;
  }, []);
  return { entries, record, forget };
}
